import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Suppression threshold for flicker-level loading states. Below this, the
 * user does not perceive a wait — reporting it as a loading event would be
 * noise. See NGTPA-24705 for rationale.
 */
const MIN_REPORT_MS = 200;

/**
 * Upper bound on a single loading span. Stale loading states — e.g. a
 * SkeletonTable left mounted by `{(!data || isLoading) && <SkeletonTable />}`
 * after a silently-failed fetch — would otherwise grow unbounded and skew
 * dashboard p95/sum. Capped spans get `loading.outcome: 'abandoned'` so they
 * can be filtered out (or counted on purpose) in Sentry.
 */
const MAX_DURATION_MS = 30_000;

/** Shape injected by the babel-loading-source plugin (or by React DevTools in dev). */
interface JsxSource {
  fileName: string;
  lineNumber: number;
}

interface UseLoadingSpanArgs {
  /** True while the loading state is visible to the user. */
  isLoading: boolean;
  /**
   * True when the loading state ended in error. Ends the span (tagged
   * `loading.outcome: 'error'`) the same way `isLoading` flipping false ends
   * it as `'completed'`. Call sites can pass raw `isError` — the hook owns
   * the "error is terminal for loading" semantic.
   */
  isError?: boolean;
  /**
   * Optional semantic identifier for this loading surface — becomes the Sentry
   * span name (`loading.<loadingSpanName>`). When omitted, falls back to a name
   * derived from `__loadingSource` (file path + line number, injected at build
   * time by the babel-loading-source plugin). Pass `loadingSpanName` explicitly
   * only when the auto-derived path isn't disambiguating enough (e.g., two
   * distinct loading phases in the same file — see NotificationsScreen's
   * `.preferences` vs `.list`).
   */
  loadingSpanName?: string;
  /**
   * Cap on this span, for a loading state that legitimately lasts longer than
   * `MAX_DURATION_MS`, such as one waiting on a request with a longer timeout.
   */
  maxDurationMs?: number;
  /** Auto-injected by babel-loading-source. Do not pass manually. */
  __loadingSource?: JsxSource;
}

function deriveNameFromSource(source: JsxSource | undefined): string | undefined {
  if (!source?.fileName) return undefined;
  // Strip up to the LAST `/src/` in the absolute path (babel passes absolute paths),
  // then drop the file extension. Greedy match is required: if a dev's path contains
  // `src` higher up (e.g. `/Users/foo/src/cms/frontend/src/components/Foo.tsx`), a
  // non-greedy match would strip too little and yield `cms/frontend/src/components/Foo`
  // instead of the intended `components/Foo`.
  const path = source.fileName.replace(/^.*\/src\//, '').replace(/\.(tsx?|jsx?)$/, '');
  return `${path}:${source.lineNumber}`;
}

/**
 * Emit a Sentry `op:ui.loading` span for the lifetime of a loading state,
 * so we can measure where users see loading UI and for how long.
 *
 * The span's start time is backdated to when the loading state actually
 * began, so the reported duration matches what the user perceives — even
 * though we defer creating the span by `MIN_REPORT_MS` to suppress flicker.
 *
 * If the loading state ends before `MIN_REPORT_MS`, no span is reported.
 */
export function useLoadingSpan({
  isLoading,
  isError,
  loadingSpanName,
  maxDurationMs = MAX_DURATION_MS,
  __loadingSource,
}: UseLoadingSpanArgs): void {
  // Resolve once per effect run — prefer explicit name, fall back to build-time-injected source.
  const resolvedName = loadingSpanName ?? deriveNameFromSource(__loadingSource) ?? 'unknown';

  // Capture latest isError so cleanup can tag the outcome correctly without
  // restarting the effect just because isError flipped. The effect itself is
  // gated on the derived `effectiveLoading` flag below.
  const isErrorRef = useRef(isError);
  isErrorRef.current = isError;

  // Error is terminal for loading: when isError flips true, the visual stops
  // being "loading" and we want the span to end (tagged 'error').
  const effectiveLoading = isLoading && !isError;

  useEffect(() => {
    if (!effectiveLoading) return undefined;

    const startMs = Date.now();
    let span: ReturnType<typeof Sentry.startInactiveSpan> | null = null;
    // clearTimeout doesn't cancel a callback that has already fired but not yet run;
    // this flag short-circuits the scheduled callback if cleanup beat it to the queue.
    let cancelled = false;
    let maxTimer: ReturnType<typeof setTimeout> | null = null;

    const startTimer = setTimeout(() => {
      if (cancelled) return;
      span = Sentry.startInactiveSpan({
        name: `loading.${resolvedName}`,
        op: 'ui.loading',
        startTime: startMs / 1000,
      });
      // Cap runaway spans (e.g., stale skeleton after failed fetch). Tag the outcome
      // so the dashboard can distinguish capped spans from naturally-ended ones.
      maxTimer = setTimeout(() => {
        if (span) {
          span.setAttribute('loading.outcome', 'abandoned');
          span.end();
          span = null;
        }
      }, maxDurationMs - MIN_REPORT_MS);
    }, MIN_REPORT_MS);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (maxTimer) clearTimeout(maxTimer);
      if (span) {
        span.setAttribute('loading.outcome', isErrorRef.current ? 'error' : 'completed');
        span.end();
      }
    };
  }, [effectiveLoading, resolvedName, maxDurationMs]);
}
